import {
  AddCircleOutlineOutlined as AddCircleOutlineOutlinedIcon,
  AutoMode as AutoModeIcon,
  AutoFixOff as AutoFixOffIcon,
  BedtimeOutlined as BedtimeOutlinedIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import {
  Divider,
  IconButton,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { filter, find, map, pick, some, transform } from 'lodash';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';

import { requisitesApi, tradersApi } from 'api';
import { requisitesGroupApi } from 'api/requisites-groups.api';
import {
  CloseDialogResult,
  CopyTextId,
  CrudTable,
  CrudTableActionType,
  DataGridColumnDefinition,
  ExpandableRequisites,
  FormikPanel,
  QueryFilters,
  dataGridColumns,
} from 'components';
import { NEW_ID } from 'constants/common.constants';
import { ROUTE_PATH } from 'constants/routes';
import {
  FilterDefinitionType,
  QueryKey,
  RequisitesStatus,
  RequisitesTabs,
  RequisitesVerificationStatus,
} from 'enums';
import {
  useMeta,
  useMutation,
  usePartialQuery,
  usePayinRequisitesUtils,
  useUser,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  FilterDefinition,
  Requisites,
  RequisitesGroup,
  RequisitesGroupQrCodeData,
} from 'types';
import { formUtils, requisitesUtils } from 'utils';

import { OrderGroupAutomationDialog } from './OrderGroupAutomationDialog';
import { RequisitesQrCodeDialog } from './RequisitesQrCodeDialog';
import { RequisitesQrCodeWrapper } from './RequisitesQrCodeWrapper';

type RequisitesGroupsFilters = Partial<{
  traderId?: string;
  id?: string;
}>;

type RequisitesState = {
  id: string;
  status: RequisitesStatus;
  verificationStatus: RequisitesVerificationStatus;
};

type RequisitesGroupsState = Record<string, RequisitesState[]>;

type Props = {
  archived?: boolean;
};

export const RequisitesGroupList: React.FC<Props> = ({ archived }) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.requisites.group_list',
  });

  const { t: tCommon } = useTranslation();

  const {
    role,
    isTrader,
    isAdmin,
    isTechOperator,
    isManager,
    isAdminOrTechOperator,
  } = useUser();

  const { orderAutomationApiHost } = useMeta();

  const { mutate: updateGroupStatus } = useMutation(
    requisitesApi.updateGroupStatus,
    {
      notifierType: 'execute',
      onSuccess: () =>
        queryClient.invalidateQueries(QueryKey.RequisitesGroupsPaginated),
    },
  );

  const queryClient = useQueryClient();
  const queryFn = useMemo(() => {
    if (archived) {
      return requisitesGroupApi.getAllArchivePaginatedAsRole(role);
    }

    return requisitesGroupApi.getAllPaginatedAsRole(role);
  }, [archived, role]);

  const { handleOpenPage, detailsUrl, isVerified } = usePayinRequisitesUtils();

  const queryResult = usePartialQuery(
    QueryKey.RequisitesGroupsPaginated,
    queryFn,
  );

  const queryResultTraders = useQuery(
    QueryKey.Traders,
    () => tradersApi.getAllAsRole(role)(),
    {
      enabled: isManager,
    },
  );

  const queryRequisitesGroupsFn = useMemo(() => {
    if (archived) {
      return requisitesGroupApi.getAllArchiveAsRole(role);
    }

    return requisitesGroupApi.getAllAsRole(role);
  }, [archived, role]);

  const queryResultGroups = useQuery(
    QueryKey.RequisitesGroups,
    queryRequisitesGroupsFn,
  );

  const { mutate: archive } = useMutation(
    requisitesGroupApi.archiveAsRole(role),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QueryKey.RequisitesGroupsPaginated);
      },
    },
  );

  const { mutate: updateOrderAutomation } = useMutation(
    requisitesGroupApi.updateOrderAutomationAsRole(role),
  );

  const [orderAutomationDialogProps, setOrderAutomationDialogProps] = useState<{
    open: boolean;
    data?: RequisitesGroup;
  }>({ open: false });

  const [qrCodeDetailsDialogProps, setQrCodeDetailsDialogProps] = useState<{
    open: boolean;
    data?: RequisitesGroupQrCodeData;
  }>({ open: false });

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuGroupId, setMenuGroupId] = useState<string | null>(null);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    groupId: string,
  ) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuGroupId(groupId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuGroupId(null);
  };

  const handleAllRequisites = useCallback(
    (item: RequisitesGroup, status: RequisitesStatus) => {
      updateGroupStatus({
        id: item.id,
        status,
      });
      handleMenuClose();
    },
    [updateGroupStatus],
  );

  const handleQrCodeGeneration = useCallback(
    (item: RequisitesGroup) => {
      setQrCodeDetailsDialogProps({
        open: true,
        data: {
          host: new URL(orderAutomationApiHost || '').hostname,
          apiKey: item.automationApiKey,
        },
      });
    },
    [orderAutomationApiHost],
  );

  const [requisitesGroupsState, setRequisitesGroupsState] =
    useState<RequisitesGroupsState>({});

  const isRequisitesEnabled = useCallback(
    (requisites: Partial<Requisites>) =>
      requisites.status !== RequisitesStatus.Blocked && isVerified(requisites),
    [isVerified],
  );

  const canChangeStatus = useMemo(
    () => (isAdminOrTechOperator || isTrader) && !archived,
    [archived, isAdminOrTechOperator, isTrader],
  );

  const isGroupEnabled = useCallback(
    (requisitesList: Partial<Requisites>[]) =>
      canChangeStatus && some(requisitesList, isRequisitesEnabled),
    [canChangeStatus, isRequisitesEnabled],
  );

  const updateLocalRequisitesStatuses = useCallback(
    (requisites: Requisites) => {
      const { groupId } = requisites;
      if (groupId && requisitesGroupsState[groupId]) {
        setRequisitesGroupsState((prev) => ({
          ...prev,
          [groupId]: map(prev[groupId], (val) =>
            val.id === requisites.id
              ? { ...val, status: requisites.status }
              : val,
          ),
        }));
      }
    },
    [requisitesGroupsState],
  );

  const handleQrCodeDialogClose = useCallback(() => {
    setQrCodeDetailsDialogProps({ open: false });
  }, []);

  const renderExpandableRequisites = useCallback(
    (requisites: Requisites) => (
      <ExpandableRequisites
        key={requisites.id}
        requisites={requisites}
        onClick={() =>
          handleOpenPage(requisites.id, detailsUrl, {
            state: {
              groupId: requisites.groupId,
              tab: RequisitesTabs.Groups,
            },
          })
        }
        onStatusUpdate={updateLocalRequisitesStatuses}
      />
    ),
    [handleOpenPage, detailsUrl, updateLocalRequisitesStatuses],
  );

  const renderStatus = useCallback(
    (item: RequisitesGroup) => {
      const groupRequisites: Partial<Requisites>[] | undefined =
        requisitesGroupsState[item.id];

      const activeRequisitesCount = filter(groupRequisites, {
        status: RequisitesStatus.Active,
      }).length;

      const availableRequisitesCount = filter(
        groupRequisites,
        isRequisitesEnabled,
      ).length;

      const hasActiveRequisites = activeRequisitesCount > 0;
      const isAllAvailableRequisitesActive =
        activeRequisitesCount === availableRequisitesCount;
      const isAllRequisitesInactive = activeRequisitesCount === 0;
      const isDisabled = !isGroupEnabled(item.requisites);

      return (
        <div className="tw-flex tw-items-center tw-justify-start">
          <Tooltip
            title={
              hasActiveRequisites
                ? t('status_details.active', {
                    activeNumber: activeRequisitesCount,
                    maxNumber: item.requisites?.length,
                  })
                : t('status_details.inactive')
            }
          >
            <div className="tw-flex-col tw-text-center tw-cursor-default tw-w-8">
              {hasActiveRequisites ? (
                <Typography color="primary">
                  {`${activeRequisitesCount}/${item.requisites.length}`}
                </Typography>
              ) : (
                <IconButton disabled>
                  <BedtimeOutlinedIcon />
                </IconButton>
              )}
            </div>
          </Tooltip>
          {!isDisabled && (
            <Fragment>
              <div className="tw-flex-col tw-text-center tw-cursor-default">
                <IconButton
                  color="primary"
                  onClick={(e) => handleMenuOpen(e, item.id)}
                >
                  <MoreVertIcon />
                </IconButton>
              </div>
              <FormikPanel
                placement="bottom-start"
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl) && menuGroupId === item.id}
                onClose={handleMenuClose}
                paperClassName="tw-border tw-border-stone-400 tw-px-2 tw-py-2 tw-w-88"
              >
                <div className="tw-flex tw-flex-col">
                  {!isAllAvailableRequisitesActive && (
                    <MenuItem
                      className="!tw-rounded-sm"
                      onClick={() =>
                        handleAllRequisites(item, RequisitesStatus.Active)
                      }
                    >
                      {t('actions.enable_all_available')}
                    </MenuItem>
                  )}
                  {!isAllAvailableRequisitesActive &&
                    !isAllRequisitesInactive && <Divider variant="middle" />}
                  {!isAllRequisitesInactive && (
                    <MenuItem
                      className="!tw-rounded-sm"
                      onClick={() =>
                        handleAllRequisites(item, RequisitesStatus.Inactive)
                      }
                    >
                      {t('actions.disable_all_available')}
                    </MenuItem>
                  )}
                </div>
              </FormikPanel>
            </Fragment>
          )}
        </div>
      );
    },
    [
      handleAllRequisites,
      isGroupEnabled,
      isRequisitesEnabled,
      menuAnchorEl,
      menuGroupId,
      requisitesGroupsState,
      t,
    ],
  );

  const columns = useMemo(
    (): DataGridColumnDefinition<RequisitesGroup>[] => [
      {
        header: t('fields.status'),
        valueGetter: renderStatus,
      },
      dataGridColumns.getIdColumn(),
      {
        header: t('fields.trader'),
        valueGetter: (item) => item.user?.name,
        hidden: isTrader,
      },
      {
        header: t('fields.name'),
        valueKey: 'name',
      },
      {
        header: t('fields.automation_api_key'),
        valueGetter: (item) => (
          <RequisitesQrCodeWrapper
            onClick={() => handleQrCodeGeneration(item)}
            title={t('order_automation_qr_dialog.hint')}
            disabled={!orderAutomationApiHost}
          >
            <CopyTextId id={item.automationApiKey} disableIconColor />
          </RequisitesQrCodeWrapper>
        ),
      },
      {
        header: t('fields.requisites'),
        multiValueRenderer: {
          itemsGetter: (item) => item.requisites,
          valueGetter: renderExpandableRequisites,
        },
        hidden: archived,
      },
      {
        header: t('fields.order_automation_type'),
        valueGetter: (item) => (
          <Fragment>
            {requisitesUtils.getOrderAutomationTypeLabel(
              item.orderAutomationType,
            )}
            {item.autoDisconnect && (
              <Tooltip title={t('fields.auto_disconnect')}>
                <AutoFixOffIcon className="tw-ml-2" />
              </Tooltip>
            )}
          </Fragment>
        ),
      },
    ],
    [
      t,
      renderStatus,
      isTrader,
      renderExpandableRequisites,
      archived,
      orderAutomationApiHost,
      handleQrCodeGeneration,
    ],
  );

  const detailsPath = useMemo(() => {
    if (isTrader) {
      return ROUTE_PATH.TRADER.PAYIN_REQUISITES_GROUP_DETAILS;
    } else if (isAdmin) {
      return ROUTE_PATH.ADMIN.PAYIN_REQUISITES_GROUP_DETAILS;
    } else if (isTechOperator) {
      return ROUTE_PATH.TECH_OPERATOR.PAYIN_REQUISITES_GROUP_DETAILS;
    }
  }, [isTrader, isTechOperator, isAdmin]);

  const handleOrderAutomation = useCallback((item: RequisitesGroup) => {
    setOrderAutomationDialogProps({ open: true, data: item });
  }, []);

  const handleOrderAutomationDialogClose = useCallback(
    (
      result: CloseDialogResult<Pick<RequisitesGroup, 'orderAutomationType'>>,
    ) => {
      if (
        isAdminOrTechOperator &&
        result.ok &&
        result.data &&
        orderAutomationDialogProps.data
      ) {
        updateOrderAutomation(
          {
            id: orderAutomationDialogProps.data.id,
            data: result.data,
          },
          {
            onSuccess: () => {
              setOrderAutomationDialogProps({ open: false });
              queryClient.invalidateQueries(QueryKey.RequisitesGroupsPaginated);
            },
          },
        );
      } else {
        setOrderAutomationDialogProps({ open: false });
      }
    },
    [
      isAdminOrTechOperator,
      orderAutomationDialogProps.data,
      queryClient,
      updateOrderAutomation,
    ],
  );

  const filtersDefinitions: FilterDefinition<RequisitesGroupsFilters>[] =
    useMemo(
      () => [
        {
          label: t('filters.trader'),
          name: 'traderId',
          type: FilterDefinitionType.Trader,
          traders: queryResultTraders.data,
          hidden: isTrader,
        },
        {
          label: t('filters.name'),
          name: 'id',
          type: FilterDefinitionType.Select,
          options: formUtils.getOptions(queryResultGroups.data || []),
          getDisplayName: (id: string) =>
            find(queryResultGroups.data, { id })?.name,
        },
      ],
      [isTrader, queryResultTraders.data, queryResultGroups.data, t],
    );

  useEffect(() => {
    if (queryResult.data?.items) {
      setRequisitesGroupsState(
        transform(
          queryResult.data.items,
          (result, item) => {
            result[item.id] = map(item.requisites, (requisites) =>
              pick(requisites as Requisites, [
                'id',
                'status',
                'verificationStatus',
              ]),
            );
          },
          {} as RequisitesGroupsState,
        ),
      );
    }
  }, [queryResult.data?.items]);

  return (
    <Fragment>
      <QueryFilters filtersDefinitions={filtersDefinitions} />
      <CrudTable
        queryResult={queryResult}
        columns={columns}
        paginated
        hideActions={(!isTrader && !isAdminOrTechOperator) || archived}
        actions={[
          {
            type: CrudTableActionType.Custom,
            hidden: !isTrader,
            render: (item) => (
              <IconButton
                title={tCommon(
                  'features.requisites.requisites_details.create_requisites.title',
                )}
                color="primary"
                onClick={() =>
                  handleOpenPage(NEW_ID, detailsUrl, {
                    state: {
                      groupId: item.id,
                      tab: RequisitesTabs.Groups,
                    },
                  })
                }
              >
                <AddCircleOutlineOutlinedIcon />
              </IconButton>
            ),
          },
          {
            type: CrudTableActionType.Custom,
            hidden: !isAdminOrTechOperator,
            render: (item) => (
              <IconButton
                color="primary"
                onClick={() => {
                  handleOrderAutomation(item);
                }}
              >
                <AutoModeIcon />
              </IconButton>
            ),
          },
          {
            type: CrudTableActionType.Details,
            path: detailsPath,
            navigateOptions: {
              state: {
                tab: RequisitesTabs.Groups,
              },
            },
          },
          {
            type: CrudTableActionType.Remove,
            onRemove: (item, { close }) =>
              archive(item.id, {
                onSuccess: close,
              }),
          },
        ]}
      />
      <OrderGroupAutomationDialog
        {...orderAutomationDialogProps}
        onClose={handleOrderAutomationDialogClose}
      />
      <RequisitesQrCodeDialog
        {...qrCodeDetailsDialogProps}
        title={t('order_automation_qr_dialog.title')}
        onClose={handleQrCodeDialogClose}
      />
    </Fragment>
  );
};
